var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { boundMethod } from 'autobind-decorator';
import memoize from 'memoize-one';
import { PureComponent } from 'react';
import { omitNullValues } from 'utils/object';
import telephony from './api/telephony';
const DEFAULT_FETCH_THROTTLE = 300;
/** The position of the new data, used when `settings.infinite` is `true` */
var NewDataPosition;
(function (NewDataPosition) {
    NewDataPosition[NewDataPosition["PreviousPage"] = -1] = "PreviousPage";
    NewDataPosition[NewDataPosition["Replace"] = 0] = "Replace";
    NewDataPosition[NewDataPosition["NextPage"] = 1] = "NextPage";
})(NewDataPosition || (NewDataPosition = {}));
export const withPhoneNumbers = (Component, settings = {}) => {
    class WithPhoneNumbers extends PureComponent {
        constructor() {
            var _a;
            super(...arguments);
            this.state = {
                items: [],
                getItemsAsyncStatus: 'succeeded',
                releaseAsyncStatus: 'succeeded',
                updateAsyncStatus: 'succeeded',
                purchaseAsyncStatus: 'succeeded',
                params: (_a = settings.initialParams) !== null && _a !== void 0 ? _a : {},
                hasNextPage: null,
                hasPreviousPage: null,
                nextPagePath: null,
                previousPagePath: null,
            };
            this.timeout = null;
            this.memoizedGetInjectedProps = memoize(this.getInjectedProps).bind(this);
            this.handleAsyncStatusUpdate = (key) => (asyncStatus) => (response) => {
                this.setState({ [key]: asyncStatus });
                return response;
            };
        }
        getItems(position = NewDataPosition.Replace) {
            const { params } = this.state;
            this.setState({ getItemsAsyncStatus: 'loading' });
            return telephony
                .getPhoneNumbers(params)
                .then((res) => this.handleQueryResolve(res, position), this.handleQueryReject);
        }
        paramsChange(params, fetchThrottle = DEFAULT_FETCH_THROTTLE) {
            return new Promise((resolve) => {
                this.setState((state) => ({
                    // remove values that are `nulled` by the new param
                    params: omitNullValues(Object.assign(Object.assign({}, state.params), params)),
                }), () => {
                    if (fetchThrottle >= 0) {
                        if (this.timeout) {
                            clearTimeout(this.timeout);
                        }
                        this.timeout = setTimeout(() => {
                            resolve(this.getItems());
                        }, fetchThrottle);
                    }
                    else {
                        resolve();
                    }
                });
            });
        }
        release(number) {
            const handlers = this.getAsyncStatusUpdateHandlers('releaseAsyncStatus');
            this.setState({ releaseAsyncStatus: 'loading' });
            return telephony.releaseNumber(number).then(...handlers);
        }
        update(query) {
            const handlers = this.getAsyncStatusUpdateHandlers('updateAsyncStatus');
            this.setState({ updateAsyncStatus: 'loading' });
            return telephony.updateNumber(query).then(...handlers);
        }
        buyNumber(query) {
            const handlers = this.getAsyncStatusUpdateHandlers('purchaseAsyncStatus');
            this.setState({ purchaseAsyncStatus: 'loading' });
            return telephony.buyNumber(query).then(...handlers);
        }
        buyGroup(query) {
            const handlers = this.getAsyncStatusUpdateHandlers('purchaseAsyncStatus');
            this.setState({ purchaseAsyncStatus: 'loading' });
            return telephony.buyGroup(query).then(...handlers);
        }
        getAsyncStatusUpdateHandlers(key) {
            const handler = this.handleAsyncStatusUpdate(key);
            return [handler('succeeded'), handler('failed')];
        }
        handleQueryResolve(response, position = NewDataPosition.Replace) {
            const { data, meta } = response;
            this.setState(({ items }) => ({
                getItemsAsyncStatus: 'succeeded',
                items: this.getPaginatedItems(data.numbers, items, position),
                nextPagePath: meta.next_page,
                previousPagePath: meta.prev_page,
                hasNextPage: !!meta.next_page,
                hasPreviousPage: !!meta.prev_page,
            }));
            return response;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        handleQueryReject(error) {
            this.setState({ getItemsAsyncStatus: 'failed' });
            return error;
        }
        getPaginatedItems(newItems, oldItems, position = NewDataPosition.Replace) {
            if (!settings.infinite || position === NewDataPosition.Replace) {
                return newItems;
            }
            if (position === NewDataPosition.NextPage) {
                return oldItems.concat(newItems);
            }
            if (position === NewDataPosition.PreviousPage) {
                return newItems.concat(oldItems);
            }
            return [];
        }
        changePath(key, position) {
            return new Promise((resolve, reject) => {
                this.setState(({ [key]: path }) => {
                    if (!path) {
                        throw new Error(`${key} does not exist`);
                    }
                    return { getItemsAsyncStatus: 'loading', params: { path } };
                }, () => void this.getItems(position).then(resolve, reject));
            });
        }
        nextPage() {
            return this.changePath('nextPagePath', NewDataPosition.NextPage);
        }
        previousPage() {
            return this.changePath('previousPagePath', NewDataPosition.PreviousPage);
        }
        getInjectedProps(items, getItemsAsyncStatus, releaseAsyncStatus, updateAsyncStatus, purchaseAsyncStatus, params, hasNextPage, hasPreviousPage, nextPagePath, previousPagePath) {
            return {
                items,
                getItemsAsyncStatus,
                releaseAsyncStatus,
                updateAsyncStatus,
                purchaseAsyncStatus,
                params,
                hasNextPage,
                hasPreviousPage,
                nextPagePath,
                previousPagePath,
                getItems: this.getItems,
                paramsChange: this.paramsChange,
                nextPage: this.nextPage,
                previousPage: this.previousPage,
                release: this.release,
                update: this.update,
                buyNumber: this.buyNumber,
                buyGroup: this.buyGroup,
            };
        }
        render() {
            const { items, getItemsAsyncStatus, releaseAsyncStatus, updateAsyncStatus, purchaseAsyncStatus, params, hasNextPage, hasPreviousPage, nextPagePath, previousPagePath, } = this.state;
            return (_jsx(Component, Object.assign({}, this.props, { phoneNumbers: this.memoizedGetInjectedProps(items, getItemsAsyncStatus, releaseAsyncStatus, updateAsyncStatus, purchaseAsyncStatus, params, hasNextPage, hasPreviousPage, nextPagePath, previousPagePath) })));
        }
    }
    __decorate([
        boundMethod
    ], WithPhoneNumbers.prototype, "getItems", null);
    __decorate([
        boundMethod
    ], WithPhoneNumbers.prototype, "paramsChange", null);
    __decorate([
        boundMethod
    ], WithPhoneNumbers.prototype, "release", null);
    __decorate([
        boundMethod
    ], WithPhoneNumbers.prototype, "update", null);
    __decorate([
        boundMethod
    ], WithPhoneNumbers.prototype, "buyNumber", null);
    __decorate([
        boundMethod
    ], WithPhoneNumbers.prototype, "buyGroup", null);
    __decorate([
        boundMethod
    ], WithPhoneNumbers.prototype, "getAsyncStatusUpdateHandlers", null);
    __decorate([
        boundMethod
    ], WithPhoneNumbers.prototype, "handleQueryResolve", null);
    __decorate([
        boundMethod
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ], WithPhoneNumbers.prototype, "handleQueryReject", null);
    __decorate([
        boundMethod
    ], WithPhoneNumbers.prototype, "changePath", null);
    __decorate([
        boundMethod
    ], WithPhoneNumbers.prototype, "nextPage", null);
    __decorate([
        boundMethod
    ], WithPhoneNumbers.prototype, "previousPage", null);
    __decorate([
        boundMethod
    ], WithPhoneNumbers.prototype, "getInjectedProps", null);
    return WithPhoneNumbers;
};
